import React, { useState, useEffect, useCallback } from 'react';
import { MetricsResponse } from '../types';
import { getMetrics } from '../services/api';
import { format } from 'date-fns';
import LoadingSpinner from './LoadingSpinner';

/**
 * MetricsPanel Component
 * 
 * Displays system health metrics for the last 24 hours.
 * Auto-refreshes every 30 seconds.
 * 
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5
 */

interface MetricsPanelProps {
  period?: '1h' | '24h' | '7d' | '30d';
  autoRefresh?: boolean;
  refreshInterval?: number; // milliseconds
}

export const MetricsPanel: React.FC<MetricsPanelProps> = ({
  period = '24h',
  autoRefresh = true,
  refreshInterval = 30000, // 30 seconds
}) => {
  const [metrics, setMetrics] = useState<MetricsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch metrics from API
   * Requirement 5.1-5.5: Display metrics for 24-hour period
   */
  const fetchMetrics = useCallback(async () => {
    try {
      setError(null);
      const data = await getMetrics(period);
      setMetrics(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load metrics');
      console.error('Error fetching metrics:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  /**
   * Initial load
   */
  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  /**
   * Auto-refresh timer
   * Requirement 6.1: Auto-refresh every 30 seconds
   */
  useEffect(() => {
    if (!autoRefresh) return;

    const timer = setInterval(() => {
      fetchMetrics();
    }, refreshInterval);

    return () => clearInterval(timer);
  }, [autoRefresh, refreshInterval, fetchMetrics]);

  /**
   * Format timestamp
   */
  const formatTimestamp = (timestamp: string): string => {
    return format(new Date(timestamp), 'yyyy-MM-dd HH:mm:ss');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">System Metrics ({period})</h2>
        <div className="flex justify-center items-center h-32">
          <LoadingSpinner message="Loading metrics..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">System Metrics ({period})</h2>
        <div className="bg-red-50 border border-red-200 rounded p-4">
          <p className="text-red-700">{error}</p>
          <button
            onClick={fetchMetrics}
            className="mt-2 px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-800">System Metrics ({period})</h2>
        <div className="text-xs text-gray-500">
          Updated: {formatTimestamp(metrics.timestamp)}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {/* Total Jobs - Requirement 5.1 */}
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-sm text-blue-600 font-medium mb-1">Total Jobs</div>
          <div className="text-2xl font-bold text-blue-900">{metrics.totalJobs}</div>
        </div>

        {/* Completed Jobs - Requirement 5.2 */}
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-sm text-green-600 font-medium mb-1">Completed</div>
          <div className="text-2xl font-bold text-green-900">{metrics.completedJobs}</div>
        </div>

        {/* Failed Jobs - Requirement 5.3 */}
        <div className="bg-red-50 rounded-lg p-4">
          <div className="text-sm text-red-600 font-medium mb-1">Failed</div>
          <div className="text-2xl font-bold text-red-900">{metrics.failedJobs}</div>
        </div>

        {/* Success Rate - Requirement 5.4 */}
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="text-sm text-purple-600 font-medium mb-1">Success Rate</div>
          <div className="text-2xl font-bold text-purple-900">
            {metrics.successRate.toFixed(2)}%
          </div>
        </div>

        {/* Average Processing Duration - Requirement 5.5 */}
        <div className="bg-yellow-50 rounded-lg p-4">
          <div className="text-sm text-yellow-600 font-medium mb-1">Avg Duration</div>
          <div className="text-2xl font-bold text-yellow-900">
            {metrics.avgProcessingDuration.toFixed(1)}s
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetricsPanel;
